import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface WorkingHours {
  start: string;
  end: string;
  enabled: boolean;
}

interface CalendarSettings {
  workingHours: {
    monday: WorkingHours;
    tuesday: WorkingHours;
    wednesday: WorkingHours;
    thursday: WorkingHours;
    friday: WorkingHours;
    saturday: WorkingHours;
    sunday: WorkingHours;
  };
  calendarView: 'fullWeek' | 'workWeek';
  notifications: {
    emailReminders: boolean;
    smsReminders: boolean;
    reminderTime: number;
    dailySchedule: boolean;
    weeklySchedule: boolean;
  };
}

interface SettingsContextType {
  settings: CalendarSettings;
  updateSettings: (newSettings: CalendarSettings) => void;
  isWorkWeek: boolean;
}

const defaultSettings: CalendarSettings = {
  workingHours: {
    monday: { start: '09:00', end: '17:00', enabled: true },
    tuesday: { start: '09:00', end: '17:00', enabled: true },
    wednesday: { start: '09:00', end: '17:00', enabled: true },
    thursday: { start: '09:00', end: '17:00', enabled: true },
    friday: { start: '09:00', end: '17:00', enabled: true },
    saturday: { start: '10:00', end: '14:00', enabled: false },
    sunday: { start: '10:00', end: '14:00', enabled: false },
  },
  calendarView: 'workWeek',
  notifications: {
    emailReminders: true,
    smsReminders: false,
    reminderTime: 24,
    dailySchedule: true,
    weeklySchedule: true,
  }
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<CalendarSettings>(defaultSettings);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('plannitas-settings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(parsedSettings);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    }
  }, []);

  const updateSettings = (newSettings: CalendarSettings) => {
    setSettings(newSettings);
    localStorage.setItem('plannitas-settings', JSON.stringify(newSettings));
  };

  const isWorkWeek = settings.calendarView === 'workWeek';

  const value: SettingsContextType = {
    settings,
    updateSettings,
    isWorkWeek
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;